import tinycolor from 'tinycolor2';

import { hexToRGB } from './hex-to-rgb';
import { interpolateDarken } from './interpolate-darken';
import { interpolateLighten } from './interpolate-lighten';

/**
 * Generates a set of color shades and their corresponding alpha variants based on a base color.
 *
 * The function creates a palette of solid color shades by lightening and darkening the base color,
 * and also generates RGBA variants for each shade using specified alpha steps.
 *
 * @param baseColor - The base color in hexadecimal format (e.g., "#3498db").
 * @param alphaSteps - An array of alpha percentage values (0-100) to generate RGBA variants for each shade.
 * Defaults to [10, 25, 50, 75, 90].
 * @returns An object containing:
 *   - `solid`: A record of shade keys (e.g., "50", "100", ..., "950") mapped to their hex color values.
 *   - `alpha`: A record of shade/alpha keys (e.g., "100/25") mapped to their RGBA color values.
 */
export const generateColors = (baseColor: string, alphaSteps: number[] = [10, 25, 50, 75, 90]) => {
  const instance = tinycolor(baseColor);

  if (!instance.isValid()) {
    throw new Error(`Invalid base color: ${baseColor}`);
  }

  const color = `#${instance.toHex()}`;

  const shades: Record<string, string> = {
    '50': interpolateLighten(color, 0.92),
    '100': interpolateLighten(color, 0.8),
    '200': interpolateLighten(color, 0.6),
    '300': interpolateLighten(color, 0.4),
    '400': interpolateLighten(color, 0.2),
    '500': color,
    '600': interpolateDarken(color, 0.2),
    '700': interpolateDarken(color, 0.35),
    '800': interpolateDarken(color, 0.5),
    '900': interpolateDarken(color, 0.65),
    '950': interpolateDarken(color, 0.78),
  };

  const shadesAlpha: Record<string, string> = {};

  for (const [shade, hex] of Object.entries(shades)) {
    const { blue, green, red } = hexToRGB(hex);

    for (const step of alphaSteps) {
      shadesAlpha[`${shade}/${step}`] = `rgba(${red}, ${green}, ${blue}, ${step / 100})`;
    }
  }

  return { alpha: shadesAlpha, solid: shades };
};
